"use client";

import { useCallback, useEffect, useId, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils";

export type SelectOption = { value: string; label: string };

type SelectProps = {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  id?: string;
  name?: string;
  className?: string;
  ariaLabel?: string;
  ariaLabelledby?: string;
};

const triggerClass =
  "relative flex h-12 w-full items-center rounded-xl border border-black/10 bg-white px-4 pr-10 text-left text-body outline-none transition-colors focus-visible:border-teal-07 focus-visible:ring-1 focus-visible:ring-teal-07 aria-expanded:border-teal-07 aria-expanded:ring-1 aria-expanded:ring-teal-07";

const nativeSelectClass =
  "h-12 w-full appearance-none rounded-xl border border-black/10 bg-white bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23999%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-size-[16px] bg-position-[right_12px_center] bg-no-repeat px-4 pr-10 text-body outline-none transition-colors focus:border-teal-07 focus:ring-1 focus:ring-teal-07";

const TOUCH_QUERIES = ["(pointer: coarse)", "(hover: none)"];
const TABLET_QUERIES = [
  "(orientation: portrait) and (min-width: 768px)",
  "(orientation: landscape) and (min-width: 1024px)",
];
const NATIVE_QUERIES = [...TOUCH_QUERIES, ...TABLET_QUERIES];

function useNativeSelect() {
  const matches = (q: string) => window.matchMedia(q).matches;
  return useSyncExternalStore(
    (onChange) => {
      const mqs = NATIVE_QUERIES.map((q) => window.matchMedia(q));
      mqs.forEach((mq) => mq.addEventListener("change", onChange));
      return () => mqs.forEach((mq) => mq.removeEventListener("change", onChange));
    },
    () => {
      const touch = TOUCH_QUERIES.some(matches) || navigator.maxTouchPoints > 0;
      return touch && !TABLET_QUERIES.some(matches);
    },
    () => false,
  );
}

export function Select({
  value,
  onChange,
  options,
  placeholder = "Select",
  id,
  name,
  className,
  ariaLabel,
  ariaLabelledby,
}: SelectProps) {
  const isTouch = useNativeSelect();
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [offset, setOffset] = useState({ left: 0, top: 0 });
  const [dropUp, setDropUp] = useState(false);
  const [maxHeight, setMaxHeight] = useState(264);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const optionRefs = useRef<(HTMLLIElement | null)[]>([]);
  const typeahead = useRef({ query: "", at: 0 });

  const baseId = useId();
  const listboxId = `${baseId}-listbox`;
  const selected = options.find((o) => o.value === value);
  const selectedIndex = options.findIndex((o) => o.value === value);

  const place = useCallback(() => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (!r) return;
    setRect(r);
    const vv = window.visualViewport;
    setOffset({ left: vv?.offsetLeft ?? 0, top: vv?.offsetTop ?? 0 });
    const viewportH = vv?.height ?? window.innerHeight;
    const spaceBelow = viewportH - r.bottom;
    const spaceAbove = r.top;
    const up = spaceBelow < 264 && spaceAbove > spaceBelow;
    setDropUp(up);
    const available = (up ? spaceAbove : spaceBelow) - 12;
    setMaxHeight(Math.max(140, Math.min(264, available)));
  }, []);

  const openMenu = useCallback(() => {
    place();
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    setOpen(true);
  }, [place, selectedIndex]);

  const closeMenu = useCallback((focusTrigger = true) => {
    setOpen(false);
    setActiveIndex(-1);
    if (focusTrigger) triggerRef.current?.focus();
  }, []);

  const commit = useCallback(
    (index: number) => {
      const opt = options[index];
      if (opt) onChange(opt.value);
      closeMenu();
    },
    [options, onChange, closeMenu],
  );

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (!triggerRef.current?.contains(target) && !listRef.current?.contains(target)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopImmediatePropagation();
        closeMenu();
      }
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    window.visualViewport?.addEventListener("resize", place);
    window.visualViewport?.addEventListener("scroll", place);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      window.visualViewport?.removeEventListener("resize", place);
      window.visualViewport?.removeEventListener("scroll", place);
    };
  }, [open, place, closeMenu]);

  useEffect(() => {
    const list = listRef.current;
    if (!open || !list) return;
    if (!document.body.hasAttribute("data-scroll-locked")) return;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      list.scrollTop += e.deltaY;
    };
    let lastY = 0;
    const onTouchStart = (e: TouchEvent) => {
      lastY = e.touches[0]?.clientY ?? 0;
    };
    const onTouchMove = (e: TouchEvent) => {
      const y = e.touches[0]?.clientY;
      if (y === undefined) return;
      e.preventDefault();
      list.scrollTop += lastY - y;
      lastY = y;
    };

    list.addEventListener("wheel", onWheel, { passive: false });
    list.addEventListener("touchstart", onTouchStart, { passive: true });
    list.addEventListener("touchmove", onTouchMove, { passive: false });
    return () => {
      list.removeEventListener("wheel", onWheel);
      list.removeEventListener("touchstart", onTouchStart);
      list.removeEventListener("touchmove", onTouchMove);
    };
  }, [open, rect]);

  useEffect(() => {
    if (open && activeIndex >= 0) {
      optionRefs.current[activeIndex]?.scrollIntoView({ block: "nearest" });
    }
  }, [open, activeIndex]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (!open) openMenu();
        else setActiveIndex((i) => Math.min(i + 1, options.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        if (!open) openMenu();
        else setActiveIndex((i) => Math.max(i - 1, 0));
        break;
      case "Home":
        if (open) {
          e.preventDefault();
          setActiveIndex(0);
        }
        break;
      case "End":
        if (open) {
          e.preventDefault();
          setActiveIndex(options.length - 1);
        }
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (open) commit(activeIndex);
        else openMenu();
        break;
      case "Tab":
        if (open) {
          setOpen(false);
          setActiveIndex(-1);
        }
        break;
      default:
        if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
          e.preventDefault();
          if (!open) openMenu();
          const t = typeahead.current;
          const now = Date.now();
          t.query = now - t.at > 800 ? e.key : t.query + e.key;
          t.at = now;
          const q = t.query.toLowerCase();
          const found = options.findIndex((o) => o.label.toLowerCase().startsWith(q));
          if (found >= 0) setActiveIndex(found);
        }
    }
  };

  if (isTouch) {
    return (
      <select
        id={id}
        name={name ?? id ?? baseId}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(nativeSelectClass, selected ? "text-black" : "text-black/55", className)}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-activedescendant={
          open && activeIndex >= 0 ? `${baseId}-opt-${activeIndex}` : undefined
        }
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        onClick={() => (open ? closeMenu() : openMenu())}
        onKeyDown={handleKeyDown}
        className={cn(triggerClass, selected ? "text-black" : "text-black/55", className)}
      >
        <span className="truncate">{selected ? selected.label : placeholder}</span>
        <svg
          aria-hidden
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#999"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={cn(
            "pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 transition-transform",
            open && "rotate-180",
          )}
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open &&
        rect &&
        createPortal(
          <ul
            ref={listRef}
            id={listboxId}
            role="listbox"
            onPointerDown={(e) => e.nativeEvent.stopImmediatePropagation()}
            style={{
              position: "fixed",
              left: rect.left + offset.left,
              width: rect.width,
              maxHeight,
              pointerEvents: "auto",
              ...(dropUp
                ? { bottom: window.innerHeight - (rect.top + offset.top) + 4 }
                : { top: rect.bottom + offset.top + 4 }),
            }}
            className="z-200 overflow-auto overscroll-contain rounded-xl border border-black/10 bg-white p-1 shadow-[0_12px_28px_-12px_rgba(0,0,0,0.25)] outline-none"
          >
            {options.map((o, i) => (
              <li
                key={o.value}
                ref={(el) => {
                  optionRefs.current[i] = el;
                }}
                id={`${baseId}-opt-${i}`}
                role="option"
                aria-selected={o.value === value}
                onPointerEnter={() => setActiveIndex(i)}
                onClick={() => commit(i)}
                className={cn(
                  "text-body flex cursor-pointer items-center justify-between gap-2 rounded-lg px-3 py-2",
                  i === activeIndex ? "bg-teal-07 text-white" : "text-black",
                )}
              >
                <span className="truncate">{o.label}</span>
                {o.value === value && (
                  <svg
                    aria-hidden
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="shrink-0"
                  >
                    <path d="m5 12 5 5L20 7" />
                  </svg>
                )}
              </li>
            ))}
          </ul>,
          document.body,
        )}
    </>
  );
}
